/* eslint-disable react/require-default-props */
import { BillDocumentSelection, BillVersion } from '@enview/interface/types/bills/Bill';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BillAPI } from '../../api';
import { SEOHead } from '../../components/head/SEOHead';
import NeedToBeLoggedIn from '../../containers/NeedToBeLoggedIn';
import { State } from '../../dux/@types';
import { setBillSidebarCurrentTab } from '../../dux/BillViewDux';
import { BillSidebarTabs } from '../../helpers/BillHelper';
import getJurisdictionAdapter from '../../helpers/jurisdictions';
import { desktopMediaQuery } from '../../helpers/mediaQueries';
import BillSidebar from './BillSidebar';
import BillTextView from './BillText/BillTextView';

type BillViewProps = {
  billId: string;
  billVersionId?: string;
  defaultTabOnLoad?: (typeof BillSidebarTabs)[keyof typeof BillSidebarTabs];
};

const BillView = (props: BillViewProps): ReactElement => {
  const { billId, billVersionId, defaultTabOnLoad } = props;

  const history = useHistory();
  const [isDesktopScreen, setIsDesktopScreen] = useState(desktopMediaQuery.matches);
  const [selectedBillVersion, setSelectedBillVersion] =
    useState<BillDocumentSelection>();

  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const openRestrictionModal = useSelector(
    (state: State) => state.modals.modalOpenLoginRestriction,
  );
  const teamId = useSelector((state: State) => state.teamMode.teamId);
  const { data: bill, isSuccess } = BillAPI.endpoints.getBill.useQuery({
    billId,
    teamId,
  });
  const dispatch = useDispatch();

  const addOrdinalEnding = (shortName: string): string => {
    if (!shortName || shortName.length < 1) return shortName;
    const lastChar = shortName.slice(-1);
    if (lastChar === '1') return shortName + 'st';
    else if (lastChar === '2') return shortName + 'nd';
    else if (lastChar === '3') return shortName + 'rd';
    else return shortName + 'th';
  };

  const handleResize = (): void => {
    setIsDesktopScreen(desktopMediaQuery.matches);
  };

  const billVersionIdUrl = window.location.pathname.split('/');

  let urlVersionNumber = '';
  if (billVersionIdUrl.length === 6) {
    // eslint-disable-next-line prefer-destructuring
    urlVersionNumber = billVersionIdUrl[5];
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (billId) {
      setSelectedBillVersion(undefined);
    }
  }, [billId]);

  useEffect(() => {
    if (defaultTabOnLoad) {
      dispatch(setBillSidebarCurrentTab(defaultTabOnLoad));
    }
  }, [defaultTabOnLoad]);

  // change url depending on bill version
  useEffect(() => {
    if (
      selectedBillVersion &&
      selectedBillVersion.documentId.toString() !== urlVersionNumber
    ) {
      // preserve email tracking for logged out modal display
      const searchParams = history.location.search;
      if (selectedBillVersion.documentType === 'additionalDocument') {
        history.push(
          `/legislative-tracking/bill/details/${billId}/doc/${selectedBillVersion.documentId}${searchParams}`,
        );
      } else if (urlVersionNumber?.length > 0) {
        history.push(
          `/legislative-tracking/bill/details/${billId}/${selectedBillVersion.documentId}${searchParams}`,
        );
      } else {
        history.replace(
          `/legislative-tracking/bill/details/${billId}/${selectedBillVersion.documentId}${searchParams}`,
        );
      }
    }
  }, [selectedBillVersion]);

  // when pressing the 'back' button
  useEffect(() => {
    const versionNumber = parseInt(urlVersionNumber, 10);
    if (versionNumber && versionNumber > 0) {
      setSelectedBillVersion({ documentType: 'version', documentId: versionNumber });
    }
  }, [urlVersionNumber]);

  const getBillVersionToDisplay = useCallback(() => {
    if (!selectedBillVersion || !bill || !bill.versions) return undefined;
    return bill.versions.find(
      (ver: BillVersion) => ver.id === selectedBillVersion.documentId,
    );
  }, [bill, selectedBillVersion]);

  // sets bill version as latest, or as a specific bill version depending on url
  const updateBillVersion = useCallback(() => {
    if (!bill || !bill.versions) {
      setSelectedBillVersion(undefined);
      return;
    }
    const jurisdictionAdapter = getJurisdictionAdapter(bill.session.state.abbreviation);
    const versions: BillVersion[] = jurisdictionAdapter.constructor.sortBillVersions(
      bill.versions,
    );
    const selectedVersion =
      versions.length > 0 ? versions[versions.length - 1] : undefined;
    if (
      billVersionId &&
      versions.find((version) => version.id === Number(billVersionId))
    ) {
      setSelectedBillVersion({
        documentType: 'version',
        documentId: Number(billVersionId),
      });
    } else {
      setSelectedBillVersion(
        selectedVersion
          ? { documentType: 'version', documentId: selectedVersion.id }
          : undefined,
      );
    }
  }, [bill]);

  useEffect(() => {
    if (isSuccess) {
      updateBillVersion();
    }
  }, [isSuccess, updateBillVersion]);

  if (!bill) {
    return <Loader loaded />;
  }

  const displayedVersion = getBillVersionToDisplay();
  const billTextView = (
    <BillTextView
      bill={bill}
      displayedVersion={displayedVersion}
      selectedBillVersion={selectedBillVersion}
      setSelectedBillVersion={setSelectedBillVersion}
    />
  );
  let sessionName = bill.session.name;
  // Integer non-year session names are confusing, turn "33" into "33rd", etc
  if (!isNaN(parseInt(sessionName)) && sessionName.length <= 3) {
    sessionName = addOrdinalEnding(sessionName);
  }
  return (
    <div className="bill-view container-fluid p-0">
      <SEOHead
        custom={{ 'bill:id': billId }}
        description={`Details on ${bill.session.state.name} ${bill.number} (${bill.session.displayName}) - ${bill.name}`}
        title={`${bill.number} - ${bill.session.state.name} (${sessionName})`}
      />
      <div className="row m-0 w-100">
        {isDesktopScreen && billTextView}
        <div
          className={`col-12 col-lg-6 sidebar${bill ? ' active' : ' inactive'}`}
          id="right_sidebar"
        >
          <BillSidebar
            bill={bill}
            billRender={billTextView}
            billVersion={displayedVersion}
            selectedBillVersion={selectedBillVersion}
            setSelectedBillVersion={setSelectedBillVersion}
          />
        </div>
        {openRestrictionModal && !orgUser && <NeedToBeLoggedIn />}
      </div>
    </div>
  );
};

export default BillView;
